import { useTranslation } from "react-i18next";
import { Package } from "lucide-react";
import type { OrderItem } from "@/lib/admin-api/orders";

// ---------------------------------------------------------------------------
// FCFA formatter
// ---------------------------------------------------------------------------

function formatFCFA(amount: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(amount)
    .concat(" FCFA");
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface OrderItemsTableProps {
  readonly items: readonly OrderItem[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrderItemsTable({ items }: OrderItemsTableProps) {
  const { t } = useTranslation("admin");

  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-stone-200 bg-white px-5 py-8 text-center">
        <Package className="mx-auto h-8 w-8 text-stone-300" />
        <p className="mt-2 text-sm text-stone-500">{t("order_detail.no_items")}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-stone-200 bg-white">
      <div className="border-b border-stone-100 px-5 py-4">
        <h3 className="font-display text-base font-semibold text-stone-900">
          {t("order_detail.items")} ({items.length})
        </h3>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-stone-100 text-xs font-medium uppercase tracking-wider text-stone-500">
              <th className="px-5 py-3">{t("order_detail.col_product")}</th>
              <th className="px-5 py-3">{t("order_detail.col_sku")}</th>
              <th className="px-5 py-3 text-center">{t("order_detail.col_qty")}</th>
              <th className="px-5 py-3 text-right">{t("order_detail.col_unit_price")}</th>
              <th className="px-5 py-3 text-right">{t("order_detail.col_line_total")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                className="border-b border-stone-50 last:border-b-0 hover:bg-stone-50/50"
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {item.product.image_url ? (
                      <img
                        src={item.product.image_url}
                        alt={`${item.product.name} — ${item.product.brand}`}
                        width={40}
                        height={40}
                        className="h-10 w-10 rounded-md border border-stone-100 object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-md border border-stone-100 bg-stone-50">
                        <Package className="h-4 w-4 text-stone-300" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate font-medium text-stone-900">
                        {item.product.name}
                      </p>
                      <p className="text-xs text-stone-500">{item.product.brand}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 font-mono text-xs text-stone-500">
                  {item.product.sku}
                </td>
                <td className="px-5 py-3 text-center">{item.quantity}</td>
                <td className="px-5 py-3 text-right">{formatFCFA(item.unit_price)}</td>
                <td className="px-5 py-3 text-right font-medium">{formatFCFA(item.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="divide-y divide-stone-100 md:hidden">
        {items.map((item) => (
          <div key={item.id} className="flex gap-3 px-4 py-3">
            {item.product.image_url ? (
              <img
                src={item.product.image_url}
                alt={`${item.product.name} — ${item.product.brand}`}
                width={48}
                height={48}
                className="h-12 w-12 shrink-0 rounded-md border border-stone-100 object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-stone-100 bg-stone-50">
                <Package className="h-5 w-5 text-stone-300" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-stone-900">
                {item.product.name}
              </p>
              <p className="text-xs text-stone-500">
                {item.product.brand} &middot; {item.product.sku}
              </p>
              <div className="mt-1 flex items-center justify-between text-sm">
                <span className="text-stone-600">
                  {item.quantity} &times; {formatFCFA(item.unit_price)}
                </span>
                <span className="font-medium text-stone-900">
                  {formatFCFA(item.line_total)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
